import React from 'react';
import {
  Button,
  CheckSmallIcon,
  Heading,
  Icon,
  MatchesMediaQuery,
  Theme,
  ThemeName,
} from '@taopix/taopix-design-system';
import {TotalPageCountSelector} from '../../../Selectors';
import {useSelector} from 'react-redux';
import {AIMode, AssistantType} from '@taopix/taopix-core';
import {AssistantHeader} from '../AssistantHeader';
import {AssistantFooter} from '../AssistantFooter';
import {AssistantContent} from '../AssistantContent';
import classNames from 'classnames';
import {useTranslation} from 'react-i18next';
import {useAssistant} from '../../Context/Assistant/AssistantContext';
import {AssistantCommonProps, AssistantNavigationProps} from '../Assistant';

type ManageAssetsStepProps = {
  projectPrice?: string;
  assistantType?: AssistantType;
} & AssistantCommonProps &
  Pick<AssistantNavigationProps, 'nextButton' | 'onClose'>;

export const WelcomeStep = ({
  projectPrice = '',
  nextButton,
  onClose,
  displayMode,
  assistantType = AssistantType.Standard,
}: ManageAssetsStepProps) => {
  const {t} = useTranslation(['assistants']);
  const assistant = useAssistant();
  const isXSmallScreen = MatchesMediaQuery(['(max-width: 400px)', '(max-height: 600px)']);
  const pageCount = useSelector(TotalPageCountSelector);

  const getAIPrice = (): string => {
    const price = assistant.getPricingEngine(AIMode.On).getAIDisplayPrice(pageCount);

    if ('' !== price) {
      return ` (+ ${price})`;
    }

    return '';
  };

  const sectionClassList = classNames(
    'flex',
    'flex-col',
    'lg:mx-sm',
    'sm:mb-md',
    'rounded-[10rem]',
    'p-lg',
    'box-border',
    'cursor-pointer',
    'hover:outline',
    'hover:outline-2',
    'hover:outline-themeBorderColour',
    'hover:outline-offset-[-2px]',
    'w-[min(360rem,100vw-30rem)]'
  );
  const editorSectionClassList = classNames(sectionClassList, 'sm:hidden');
  const ulClassList = classNames(
    'space-y-xs',
    isXSmallScreen ? ['text-sm', 'leading-none', 'my-sm'] : ['my-lg']
  );
  const liClassList = classNames('flex', 'items-start');

  const editorUlClassList = classNames(ulClassList, 'sm:hidden');

  const assistantPanelContent = {
    [AssistantType.Standard]: {
      title: t('str_TitleTryDesignAssistant'),
      image: require('../../../Images/auto-flow.svg').default,
      bullets: [t('str_MessageAutomaticallyPlacesYourPicturesIntoTheDesign')],
      buttonLabel: t('str_LabelUseDesignAssistant'),
    },
    [AssistantType.Advanced]: {
      title: t('str_TitleTryDesignAssistant'),
      image: require('../../../Images/auto-flow.svg').default,
      bullets: [
        t('str_MessageAutomaticallySelectsPageLayout'),
        t('str_MessagePlacesYourPicturesIntoTheDesign'),
      ],
      buttonLabel: t('str_LabelUseDesignAssistant'),
    },
    [AssistantType.SmartDesign]: {
      title: `${t('str_TitleTrySmartDesignAssistant')}${getAIPrice()}`,
      image: require('../../../Images/smart-design.svg').default,
      bullets: [
        t('str_MessageAutomaticallyDesignsPages'),
        t('str_MessageIntelligentPictureSelection'),
        t('str_MessageFindsBestCrop'),
      ],
      buttonLabel: t('str_LabelUseSmartDesignAssistant'),
    },
  };

  const responsiveCheckIcon = (
    <Icon icon={<CheckSmallIcon />} size={isXSmallScreen ? 'small' : 'medium'} />
  );

  const bulletTextClasses = classNames('flex', 'items-center', 'leading-snug', 'mt-[2px]');

  return (
    <>
      <AssistantHeader aiMode={AIMode.Off} displayMode={displayMode} projectPrice={projectPrice} />
      <AssistantContent>
        <div className="flex flex-1 items-center justify-center relative">
          <div className="flex sm:flex-col sm:items-center lg:justify-center">
            <Theme
              name={ThemeName.Prominent}
              className={sectionClassList}
              onClick={nextButton.props.onClick}
            >
              <Heading size={isXSmallScreen ? 3 : 2} className={'mb-lg'}>
                {assistantPanelContent[assistantType].title}
              </Heading>

              <img
                className="self-center"
                src={assistantPanelContent[assistantType].image}
                width="230"
                height="169"
                alt={'An illustration of a finished photobook.'}
              />

              <ul className={ulClassList}>
                {assistantPanelContent[assistantType].bullets.map(
                  (label: string, index: number) => {
                    return (
                      <li className={liClassList} key={index}>
                        {responsiveCheckIcon}
                        <p className={bulletTextClasses}>{label}</p>
                      </li>
                    );
                  }
                )}
              </ul>

              <Button
                label={assistantPanelContent[assistantType].buttonLabel}
                buttonStyle="primary"
                onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                  event.stopPropagation();
                  nextButton.props.onClick(event);
                }}
                className={'w-full mt-auto'}
                size={isXSmallScreen ? 'small' : 'medium'}
              />
            </Theme>
            <Theme
              name={ThemeName.Container}
              className={editorSectionClassList}
              onClick={() => onClose()}
            >
              <Heading size={2} className={'mb-lg'}>
                {t('str_TitleGoStraightToTheEditor')}
              </Heading>

              {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
              <img
                className="self-center"
                src={require('../../../Images/straight-to-editor.svg').default}
                width="230"
                height="169"
                alt={'A photobook editor with a cursor dragging a picture into a page.'}
              />

              <ul className={editorUlClassList}>
                <li className={liClassList}>
                  <Icon icon={<CheckSmallIcon />} />
                  <p className={bulletTextClasses}>{t('str_MessageUseTheEditingTools')}</p>
                </li>
                {AssistantType.SmartDesign === assistantType && (
                  <li className={liClassList}>
                    <Icon icon={<CheckSmallIcon />} />
                    <p className={bulletTextClasses}>{t('str_MessageChangeMindLater')}</p>
                  </li>
                )}
              </ul>

              <Button
                buttonStyle={'secondary'}
                label={t('str_LabelGoStraightToTheEditor')}
                onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                  event.stopPropagation();
                  onClose();
                }}
                className="w-full mt-auto"
              />
            </Theme>
            <Button buttonStyle={'link'} onClick={() => onClose()} className={'lg:hidden'}>
              {t('str_LabelOrGoStraightToTheEditor')}
            </Button>
          </div>
        </div>
      </AssistantContent>
      <AssistantFooter />
    </>
  );
};
